import React, { useCallback, useEffect } from "react";
import { Locale } from "../../lib/i18n/i18n_provider";
import { PartnerOffering } from "../../data/partner_offering";
import { useQueryString } from "../../hooks";
import { OfferingParams } from "../../offering/template_offering_checkout_common";
import { useAnalytics } from "../../lib/analytics/analytics";

interface PromoCodeBoxProps {
  partnerOffering: PartnerOffering;
  spaceID: string | undefined;
  locale: Locale;
  locationID?: string | undefined;
}

export function PromoCodeBox(props: PromoCodeBoxProps) {
  const locationPromoCodeBoxContent =
    props.partnerOffering?.offeringLocations.find(
      (ol) => ol.spaceID === props.spaceID
    )?.promoCodeBoxContent;

  const membershipLocationPCBContent =
    props.partnerOffering?.offeringLocations.find(
      (ol) =>
        ol.offeringItemType === "Membership" &&
        ol.membership?.locatable?.id === props.locationID
    )?.promoCodeBoxContent;

  const promoCodeBoxContent =
    membershipLocationPCBContent ||
    locationPromoCodeBoxContent ||
    props.partnerOffering?.promoCodeBoxContent ||
    "";
  const queryString = useQueryString<OfferingParams>();

  const contentClickHandler = useCallback(
    (e: any) => {
      const closestLink = e.target.closest("a");
      if (!closestLink) return;
      const targetUrl = new URL(closestLink);

      if (targetUrl.href.includes("mailto")) {
        // If the link is a mailto link, do not override it
        return;
      }

      /** This function to find the URL in the Promo Content
       * and add additional params (fromCatalog) to the existed URL
       */
      e.preventDefault();
      const queryParams = new URLSearchParams(targetUrl.search);
      if (queryString.fromCatalog) {
        queryParams.append("fromCatalog", queryString.fromCatalog);
      }
      window.location.assign(
        `${targetUrl.origin}${targetUrl.pathname}?${queryParams.toString()}`
      );
    },
    [queryString.fromCatalog]
  );

  const analytics = useAnalytics();

  const onLinkClick = useCallback(
    (e) => {
      analytics.event(
        "Promotion Box Link Click",
        {
          "Promotion Call Box": e.target.href,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [analytics]
  );

  useEffect(() => {
    if (!promoCodeBoxContent) {
      return;
    }

    const containerEl = document.getElementById(
      "template-offering-details-base-offering-promo"
    );

    if (!containerEl) return;

    const linkEl = containerEl.querySelector("a");

    if (!linkEl) return;

    // mousedown is to track normal clicks as well as middle mouse click
    linkEl.addEventListener("mousedown", onLinkClick);
  }, [onLinkClick, promoCodeBoxContent]);

  if (!promoCodeBoxContent) {
    return null;
  }
  const promoCodeBoxContentHasManyLines =
    promoCodeBoxContent.split("\n\n").length > 1;

  return (
    <div className="row promo-box">
      <div
        className={`col ${
          !promoCodeBoxContentHasManyLines ? "text-center" : ""
        }`}
        id={"template-offering-details-base-offering-promo"}
      >
        <div
          className="bg-info pt-3 px-3"
          style={{ paddingBottom: 1 }}
          onClick={contentClickHandler}
          dangerouslySetInnerHTML={{
            __html: promoCodeBoxContent,
          }}
        />
      </div>
    </div>
  );
}

export function IntelligentOfficeRichardPromo() {
  const virtualMailAddressMembershipHref =
    "https://partners.flexspace.ai/offerings/intelligent-office-richard-virtual-office-membership?";
  const professionalBusinessAddressMembershipHref =
    "https://partners.flexspace.ai/offerings/intelligent-office-richard-professional-business-address";

  return (
    <div className="row promo-box">
      <div
        className="col text-center "
        id={"template-offering-details-base-offering-promo"}
      >
        <div className="bg-info pt-3 px-3 pb-1">
          <p>
            Welcome to Intelligent Office! As a thank you for purchasing a phone
            service plan, take 15% off a{" "}
            <a
              href={virtualMailAddressMembershipHref}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              Virtual Mail Address Membership
            </a>{" "}
            or a{" "}
            <a
              href={professionalBusinessAddressMembershipHref}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              Professional Business Address Membership
            </a>{" "}
            with code <strong>PHONE15</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}
